import { useEffect } from 'react';
import { useGlobalStateSelector, words } from './globalState';
import { writeNfcrwCallbackToLocalStorage } from './nativeApp';
import { useNfcrwScan } from './nativeAppHooks';
import iconKeyCard from './image/playing_cards_FILL0_wght400_GRAD0_opsz24.svg';

// iPhone アプリ（nfcReaderWriter）での 2 段の書き込みボタン（useNfcrwWrite と組で使う）
// 1/2: 鍵交換（アプリは NFC に触れず、公開鍵だけ返してくる）
// 2/2: 暗号化済みデータを渡して書き込み。この intent の 2/2 のリンクができていれば 2/2 を出す
// 設定画面の DisplayMnemonic、キーボックス登録直後のニーモニック確認、WIF 表示のポップアップで使う
function NativeAppWriteButton({ nfcrwWrite, intent, label }) {
  const dispatch = useGlobalStateSelector(({ dispatch }) => dispatch);
  const language = useGlobalStateSelector(({ state }) => state.language);
  let actionButton;

  if (nfcrwWrite.writeLink !== null && nfcrwWrite.writeIntent === intent) {
    actionButton =
    <a className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
      href={ nfcrwWrite.writeLink }
      onClick={ (event) => {
        if (!nfcrwWrite.armWrite(event)) {
          return;
        }

        dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithNfcTag[language] });
      }}
    >
      <div>
        { words.write2of2[language] }
      </div>
      <div>
        <img className='size2x2' src={iconKeyCard} alt='' />
      </div>
    </a>;
  }
  else {
    actionButton =
    <a className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
      href={ nfcrwWrite.handshakeLink }
      onClick={ (event) => {
        if (!nfcrwWrite.armHandshake(event, { intent })) {
          return;
        }

        dispatch({ type: 'setNotification', key: 'notification', value: words.exchangingEncryptionKeysWithTheApp[language] });
      }}
    >
      <div>
        { words.write1of2[language] }
      </div>
      <div>
        <img className='size2x2' src={iconKeyCard} alt='' />
      </div>
    </a>;
  }

  return (
    <>
      <div className='widthMax marginTop1 '>
        { label }
      </div>
      { actionButton }
    </>
  );
}

// iPhone アプリ（nfcReaderWriter）での読み取りボタン（useNfcrwScan と組で使う）
// アプリが読み取り結果を暗号化して返し、ブラウザで復号する。結果は useNfcrwScan の onRecords で受け取る
// 押したときの通知は、intent が 'keycard' ならキーカードで、それ以外は NFC タグでタッチするよう促す
// - label: ボタンの上の見出し。省略すると出さない
// - face: ボタンの中身。省略すると「読み込む」とキーカードのアイコン
// - marginTop: ボタンの上の余白（marginTop1）。既定は付ける
// - canStart: アプリへ出る前の確認。false を返すとアプリへ出ない
// - onStart: アプリへ出るとき（通知の前）に呼ぶ
function NativeAppReadButton({ nfcrwScan, intent, label, face, marginTop = true, canStart, onStart }) {
  const dispatch = useGlobalStateSelector(({ dispatch }) => dispatch);
  const language = useGlobalStateSelector(({ state }) => state.language);
  let buttonClass = 'buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ';
  let buttonFace;
  let notification;

  if (marginTop) {
    buttonClass += 'marginTop1 ';
  }

  if (face !== undefined) {
    buttonFace = face;
  }
  else {
    buttonFace =
    <>
      <div>
        { words.read[language] }
      </div>
      <div>
        <img className='size2x2' src={iconKeyCard} alt='' />
      </div>
    </>;
  }

  if (intent === 'keycard') {
    notification = words.touchWithYourKeyCard[language];
  }
  else {
    notification = words.touchWithNfcTag[language];
  }

  const button =
  <a className={ buttonClass } tabindex='0'
    href={ nfcrwScan.scanLink }
    onClick={ (event) => {
      if (canStart !== undefined && !canStart()) {
        event.preventDefault();
        return;
      }

      if (!nfcrwScan.armScan(event, { intent })) {
        return;
      }

      if (onStart !== undefined) {
        onStart();
      }

      dispatch({ type: 'setNotification', key: 'notification', value: notification });
    }}
  >
    { buttonFace }
  </a>;

  if (label === undefined) {
    return button;
  }

  return (
    <>
      <div className='widthMax marginTop1 '>
        { label }
      </div>
      { button }
    </>
  );
}

// iPhone アプリ（nfcReaderWriter）でキーカードを読むポップアップ ; type: 'nativeAppKeyCardReaderPopup'
// 出品・購入・履歴などの署名でキーカードが要るとき（nfcNativeRequired）に、getSecretsFromNativeApp が開く
// 読めたらポップアップを閉じ、レコード（Web NFC の形）を body.resolve で getSecretsFromNativeApp に返す
function NativeAppKeyCardReaderPopup({ popupLayer }) {
  const resolve = useGlobalStateSelector(({ state }) => state.popup[popupLayer]?.body?.resolve);
  const walletType = useGlobalStateSelector(({ state }) => state.walletType);
  const language = useGlobalStateSelector(({ state }) => state.language);
  const dispatch = useGlobalStateSelector(({ dispatch }) => dispatch);

  const nfcrwScan = useNfcrwScan({
    enabled: walletType === 'nfcNative',
    language,
    onRecords: ({ records }) => {
      dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: { type: null, body: null } });
      resolve(records);
    },
    onFailure: () => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToReadNfc[language] });
    },
  });

  return (
    <div className='flexColumn alignItemsCenter widthMax padding0p5'>
      <NativeAppReadButton
        nfcrwScan={nfcrwScan}
        intent='keycard'
        label={words.readKeyCardWithNativeApp[language]}
      />
    </div>
  );
}

// ネイティブアプリからの復帰タブ。結果を localStorage に書いて案内だけ出す
function NfcCallback() {
  const language = useGlobalStateSelector( ({ state }) => state.language || 'english' );

  useEffect( () => {
    const url = new URL(window.location.href);
    const state = url.searchParams.get('state');
    const nfc = url.searchParams.get('nfc');
    const error = url.searchParams.get('error');
    const pubkey = url.searchParams.get('pubkey');
    const ciphertext = url.searchParams.get('ciphertext');

    if (state && (nfc || error || pubkey)) {
      writeNfcrwCallbackToLocalStorage({ state, nfc, error, pubkey, ciphertext });
    }

    url.search = '';
    window.history.replaceState({}, '', url);
  }, []);

  return (
    <div className='flexColumn alignItemsCenter marginTop1 padding0p5'>
      { words.pleaseReturnToTheOriginalTabAndCloseThisTab[language] }
    </div>
  );
}

export {
  NativeAppWriteButton,
  NativeAppReadButton,
  NativeAppKeyCardReaderPopup,
  NfcCallback,
};
